import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Fuel, Receipt } from "lucide-react";
import { toast } from "sonner";
import { Chip, Empty, Field, Loading, NSelect, Plate, useFetch } from "@/components/common";
import { ConfirmDialog } from "@/components/DemoCleanup";
import { SecureImg, Lightbox, uploadPhoto } from "@/components/Photos";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, fDate, fEur, fNum } from "@/lib/api";

export const PLEIN_STATUS = {
  justificatif_present: ["Justificatif présent", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  justificatif_manquant: ["Justificatif manquant", "bg-rose-50 text-rose-700 border-rose-200"],
  a_verifier: ["À vérifier", "bg-amber-50 text-amber-700 border-amber-200"],
  valide: ["Validé", "bg-[#E8F1FB] text-[#1d5fa8] border-[#2F80ED]/30"],
};

export const PleinStatus = ({ v }) => <Chip testid="plein-status-chip" cls={PLEIN_STATUS[v]?.[1]}>{PLEIN_STATUS[v]?.[0] || v}</Chip>;

export const warningsOf = (e) => (e?.response?.status === 409 ? e.response.data.detail.warnings : null);

export function WarningsBox({ warnings, testid = "consumption-warnings" }) {
  if (!warnings?.length) return null;
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid={testid}>
      <div className="font-semibold flex items-center gap-1 mb-1"><AlertTriangle className="w-4 h-4" /> Contrôles à vérifier</div>
      <ul className="list-disc pl-5 space-y-0.5">{warnings.map((w) => <li key={w}>{w}</li>)}</ul>
      <p className="text-xs mt-2">Corrigez la saisie, ou confirmez exceptionnellement : la saisie restera marquée « anomalie confirmée » dans l'historique.</p>
    </div>
  );
}

function StatusDialog({ t, onClose, onDone }) {
  const [s, setS] = useState(t.control_status);
  const [note, setNote] = useState("");
  const run = async () => {
    try {
      await api.patch(`/pleins/${t.id}/status`, { status: s, note });
      toast.success("Statut mis à jour");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <ConfirmDialog title="Contrôle du plein" onConfirm={run} onClose={onClose} label="Enregistrer" testid="plein-status-dialog" primary wide>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>{t.file_id ? <SecureImg id={t.file_id} className="w-full max-h-80 object-contain rounded border bg-slate-50" testid="plein-receipt-img" /> : <Empty text="Aucun justificatif" />}</div>
        <div className="space-y-2 text-sm">
          <div><b>{t.user_name}</b> · <Plate>{t.plate}</Plate></div>
          <div>{fDate(t.date)} {t.time || ""} · {t.station || "—"} · {t.fuel || "—"}</div>
          <div>{t.mileage ? `${fNum(t.mileage)} km` : "Km non renseigné"} · {fNum(t.liters, 2)} L · {fEur(t.amount_ttc)} · {t.price_per_liter ? `${fNum(t.price_per_liter, 3)} €/L` : "—"}</div>
          {t.amount_ht != null && <div className="text-xs text-slate-500">HT {fEur(t.amount_ht)} · TVA {fEur(t.vat)}</div>}
          {t.comment && <div className="text-xs italic">« {t.comment} »</div>}
          {t.warnings?.length > 0 && <WarningsBox warnings={t.warnings} testid="plein-dialog-warnings" />}
          <Field label="Statut"><NSelect value={s} onChange={setS} testid="plein-status-select" options={Object.entries(PLEIN_STATUS).map(([value, [label]]) => ({ value, label }))} /></Field>
          <Field label="Note (facultatif)"><Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="plein-status-note" /></Field>
          {t.history?.length > 0 && (
            <div className="text-xs text-slate-500 border-t pt-2" data-testid="plein-history">
              {t.history.map((h, i) => <div key={i}>{fDate(h.at)} — {h.by} : {h.note || Object.entries(h.changes || {}).map(([k, [a, b]]) => `${k} ${a ?? "—"} → ${b}`).join(", ")}</div>)}
            </div>
          )}
        </div>
      </div>
    </ConfirmDialog>
  );
}

export function PleinsTable({ rows, admin, onChanged, testid = "pleins-table" }) {
  const [open, setOpen] = useState(null);
  const [img, setImg] = useState(null);
  if (!rows) return <Loading />;
  if (!rows.length) return <Empty text="Aucun plein Fleet Pro" />;
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="tbl w-full">
        <thead><tr><th>Date</th>{admin && <th>Commercial</th>}<th>Véhicule</th><th>Station</th><th className="text-right">Km</th><th className="text-right">Litres</th><th className="text-right">TTC</th><th className="text-right">€/L</th><th>Ticket</th><th>Statut</th><th>Source</th>{admin && <th></th>}</tr></thead>
        <tbody>{rows.map((t) => (
          <tr key={t.id} data-testid={`plein-row-${t.id}`}>
            <td className="whitespace-nowrap">{fDate(t.date)} <span className="text-xs text-slate-400">{t.time || ""}</span></td>
            {admin && <td className="font-semibold">{t.user_name}</td>}
            <td>{t.plate ? <Plate>{t.plate}</Plate> : "—"}</td>
            <td className="text-xs">{t.station || "—"}</td>
            <td className="text-right tabular-nums">{t.mileage ? fNum(t.mileage) : "—"}{t.anomaly_confirmed && <AlertTriangle className="inline w-3.5 h-3.5 ml-1 text-amber-600" data-testid="plein-anomaly-flag" />}</td>
            <td className="text-right tabular-nums">{fNum(t.liters, 2)}</td>
            <td className="text-right tabular-nums font-semibold">{fEur(t.amount_ttc)}</td>
            <td className="text-right tabular-nums">{t.price_per_liter ? fNum(t.price_per_liter, 3) : "—"}</td>
            <td>{t.file_id ? <button onClick={() => setImg(t.file_id)} className="text-[#2F80ED]" data-testid={`plein-receipt-${t.id}`}><Receipt className="w-4 h-4" /></button> : "—"}</td>
            <td><PleinStatus v={t.control_status} /></td>
            <td className="text-xs text-slate-500">{t.source_label}</td>
            {admin && <td><Button size="sm" variant="outline" onClick={() => setOpen(t)} data-testid={`plein-control-${t.id}`}>Contrôler</Button></td>}
          </tr>
        ))}</tbody>
      </table>
      <Lightbox id={img} onClose={() => setImg(null)} />
      {open && <StatusDialog t={open} onClose={() => setOpen(null)} onDone={() => { setOpen(null); onChanged?.(); }} />}
    </div>
  );
}

export function AdminPleins() {
  const [f, setF] = useState({});
  const users = useFetch("/users", { role: "commercial", with_m2: true });
  const { data, reload } = useFetch("/pleins", f);
  const set = (k) => (v) => setF({ ...f, [k]: v });
  return (
    <div className="space-y-3" data-testid="admin-pleins">
      <div className="flex flex-wrap gap-3 items-end">
        <Field label="Commercial"><NSelect value={f.user_id} onChange={set("user_id")} placeholder="Tous" testid="pleins-filter-user" options={(users.data || []).map((u) => ({ value: u.id, label: u.name }))} /></Field>
        <Field label="Statut"><NSelect value={f.status} onChange={set("status")} placeholder="Tous" testid="pleins-filter-status" options={Object.entries(PLEIN_STATUS).map(([value, [label]]) => ({ value, label }))} /></Field>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl"><PleinsTable rows={data} admin onChanged={reload} /></div>
    </div>
  );
}

export function PleinCta({ testid = "cta-enregistrer-plein" }) {
  return (
    <Link to="/commerciaux/plein" data-testid={testid} className="flex items-center justify-center gap-2 h-12 rounded-xl bg-[#F49619] hover:bg-[#E0830B] text-white font-semibold transition-colors">
      <Fuel className="w-5 h-5" /> Enregistrer un plein
    </Link>
  );
}

export function ReceiptReplace({ t, onDone }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const id = await uploadPhoto(file, "receipt");
      await api.patch(`/pleins/${t.id}/receipt`, { file_id: id });
      toast.success("Justificatif remplacé");
      onDone();
    } catch (er) {
      toast.error(errMsg(er));
    } finally {
      setBusy(false);
    }
  };
  if (t.control_status === "valide") return <CheckCircle2 className="w-4 h-4 text-[#2F80ED]" />;
  return (
    <label className="text-xs font-semibold text-[#E0830B] cursor-pointer hover:underline" data-testid={`replace-receipt-${t.id}`}>
      {busy ? "Envoi…" : "Remplacer le ticket"}
      <input type="file" accept="image/*" className="hidden" onChange={pick} />
    </label>
  );
}
